import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../../api';
import { descartarLlPonto, gravarLlPontos, pesarSeca } from '../../api/triviais';
import { BlocoDaFicha, JanelaDeDescarte, useEquipamentos } from '../../Ficha';
import { numero, paraNumero, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { Achado, Plano, Reta, useJanela } from '../../Grafico';
import type { Ponto } from '../../Grafico';
import {
  CampoNumerico, ErroDaApi, EscolhaPorCodigo, Selo, TabelaDeLancamento, comoCodigo,
} from '../../ui';
import type { ColunaDeLancamento, LinhaDeLancamento } from '../../ui';
import { MolduraDaRepeticao, comSinal, useRepeticao } from './Repeticao';
import type { Comparacao, UseRepeticao } from './Repeticao';

/**
 * A repetição do limite de liquidez na Interpretação (onda 323, tela A).
 *
 * O lançamento à esquerda, na ordem da bancada (D9): golpes, cápsula, úmida +
 * cápsula, seca + cápsula, w. Cada ponto vai pela rota de sempre do LL — o
 * banco põe a linha na tentativa aberta. Sem a seca, o ponto «espera a seca»
 * (que se digita ali mesmo ou entra pela Bancada › Estufa).
 *
 * O gráfico à direita: a inicial vazada, com a reta tracejada (pode
 * esconder); a atual cheia, com a reta do servidor (`reta_inclinacao`,
 * `reta_intercepto`: w = a·ln N + b). O ponto que está sendo digitado
 * aparece vazado e FORA da conta (D6, opção 2): é calculado só aqui, das
 * massas e da tara da cápsula, e nunca mexe no «Atual» — o número que vale
 * volta do servidor quando o ponto é lançado.
 *
 * A faixa da NBR 6459 vale como no lançamento (D8): o ponto fora de 15 a 35
 * golpes entra marcado, e a pendência avisa.
 *
 * Quem lança: quem tem «lançar dados» e, com a repetição ABERTA, também quem
 * interpreta (decisão R1 da onda 323 — o responsável técnico não lança no
 * ensaio, mas lança na repetição que abriu). A Pesagem úmida e a Pesagem
 * seca, com a repetição aberta, também gravam nela (R2).
 */

/** NBR 6459:2025 — a faixa de golpes e o mínimo de pontos (D8). */
const FAIXA = { min: 15, max: 35, pontos: 5 } as const;
const CAT_CAPSULA = 'capsula';

const vivo = (p: any) => !(p.descartado || p.descartada);
const naEstufa = (p: any) => vivo(p) && p.massa_capsula_solo_seco == null;
const llDe = (f: any): number | null =>
  (f?.cabecalho?.limite_liquidez != null ? Number(f.cabecalho.limite_liquidez) : null);
const llTexto = (v: number | null) => (v != null ? numero(v, 0) : '—');
const pontosDoGrafico = (f: any): Ponto[] => ((f?.pontos ?? []) as any[])
  .filter((p) => vivo(p) && p.umidade != null)
  .map((p) => ({ x: Number(p.golpes), y: Number(p.umidade) }));
const retaDe = (f: any): { a: number; b: number } | null => {
  const a = f?.cabecalho?.reta_inclinacao;
  const b = f?.cabecalho?.reta_intercepto;
  return a != null && b != null ? { a: Number(a), b: Number(b) } : null;
};

interface Rascunho { golpes: string; cap: string; umido: string; seco: string }
const vazio = (): Rascunho => ({ golpes: '', cap: '', umido: '', seco: '' });

/** w = (Mu − Ms) / (Ms − Mt) × 100 — só para MOSTRAR o ponto em digitação. */
export function umidadeNaTela(umido?: number, seco?: number, tara?: number | null): number | null {
  if (umido == null || seco == null || tara == null) return null;
  if (!(seco > tara) || umido < seco) return null;
  return ((umido - seco) / (seco - tara)) * 100;
}

/**
 * A tara da cápsula: a de um ponto já gravado com ela (qualquer tentativa)
 * ou, na falta, o valor vigente da calibração interna (`/equipamentos/:id/metrologia`).
 */
export function useTaras(conhecidas: Map<string, number>, capId: string) {
  const [lidas, setLidas] = useState<Map<string, number | null>>(new Map());
  useEffect(() => {
    if (!capId || conhecidas.has(capId) || lidas.has(capId)) return undefined;
    let ativo = true;
    void api<any>(`/equipamentos/${capId}/metrologia`).then((m) => {
      const v = (m?.valores ?? []).find((x: any) => x.grandeza === 'massa' && x.vigencia_fim == null);
      if (ativo) setLidas((l) => new Map(l).set(capId, v ? Number(v.valor) : null));
    }).catch(() => { if (ativo) setLidas((l) => new Map(l).set(capId, null)); });
    return () => { ativo = false; };
  }, [capId, conhecidas, lidas]);
  return conhecidas.get(capId) ?? lidas.get(capId) ?? null;
}

// ══ os pontos em leitura (a inicial, as tentativas anteriores) ════════

const COLUNAS: ColunaDeLancamento[] = [
  { texto: 'Golpes', num: true },
  { texto: 'Cápsula' },
  { texto: 'Úmida + cáp. (g)', num: true },
  { texto: 'Seca + cáp. (g)', num: true },
  { texto: 'w (%)', num: true },
];

export function PontosDoLl({ pontos, rotulo }: { pontos: any[]; rotulo: string }) {
  return (
    <TabelaDeLancamento rotulo={rotulo} classe="capsulas fe-lanc repeticao-leitura"
      colunas={COLUNAS}
      linhas={[...pontos].sort((a, b) => a.ordem - b.ordem).map((p) => ({
        chave: p.id,
        classe: vivo(p) ? undefined : 'descartada',
        titulo: <>{p.golpes} golpes · <span className="cod">{p.capsula}</span></>,
        resultado: p.umidade != null ? <span className="n">{numero(p.umidade, 2)} %</span> : undefined,
        semCartao: p.umidade != null ? [0, 1, 4] : [0, 1],
        celulas: [
          p.golpes,
          <span className="cod">{p.capsula}</span>,
          numero(p.massa_capsula_solo_umido, 2),
          p.massa_capsula_solo_seco != null ? numero(p.massa_capsula_solo_seco, 2) : '—',
          !vivo(p) ? 'descartado' : p.umidade != null ? numero(p.umidade, 2) : 'espera a seca',
        ],
      }))} />
  );
}

// ══ a tela ════════════════════════════════════════════════════════════

export function RepeticaoLl({ id, podeInterpretar, congelado, aoMudar, children }: {
  id: string;
  podeInterpretar: boolean;
  congelado: boolean;
  aoMudar(): void | Promise<void>;
  /** A interpretação de sempre (sem repetição aberta). */
  children: ReactNode;
}) {
  const equip = useEquipamentos();
  /* R1: com a repetição aberta, quem interpreta também lança nela (a API
     aceita `ensaio.lancar` OU «interpretar com repetição aberta»). */
  const { pode } = useSessao();
  const lanca = pode('ensaio.lancar') || pode('ensaio.interpretar');
  const [rascunho, setRascunho] = useState<Rascunho>(vazio);
  const [mostrarInicial, setMostrarInicial] = useState(true);
  const [secas, setSecas] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [descartando, setDescartando] = useState<any | null>(null);
  const rep = useRepeticao(id, 'limite-liquidez', aoMudar);

  /* As taras que os pontos gravados já trazem, de qualquer tentativa. */
  const conhecidas = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of [rep.dados?.inicial, rep.dados?.atual]) {
      for (const p of (f?.pontos ?? []) as any[]) {
        if (p.tara != null && !m.has(p.capsula_equipamento_id)) {
          m.set(p.capsula_equipamento_id, Number(p.tara));
        }
      }
    }
    return m;
  }, [rep.dados]);

  const opcoes = useMemo(() => equip.filter((e) => e.categoria === CAT_CAPSULA)
    .map((e) => comoCodigo({ valor: e.id, texto: e.codigo_patrimonial, detalhe: e.nome })),
  [equip]);
  const tara = useTaras(conhecidas, rascunho.cap);
  const mexer = (c: keyof Rascunho, v: string) => setRascunho((r) => ({ ...r, [c]: v }));

  const golpes = paraNumero(rascunho.golpes);
  const umido = paraNumero(rascunho.umido);
  const seco = paraNumero(rascunho.seco);
  const wDigitado = umidadeNaTela(umido, seco, tara);
  const digitando: Ponto | null = golpes != null && golpes > 0 && wDigitado != null
    ? { x: golpes, y: wDigitado } : null;
  const foraDaFaixa = golpes != null && (golpes < FAIXA.min || golpes > FAIXA.max);

  /* O que acabou de gravar sai do rascunho LOGO (`limpar`), antes das
     releituras: senão o que já se digita na linha seguinte se perderia. */
  const executar = async (
    marca: string, chamada: () => Promise<unknown>, r: UseRepeticao, limpar?: () => void,
  ) => {
    setSalvando(marca); setErro(null);
    /* A ficha relê também (sem esperar): os resumos das etapas de bancada
       contam os pontos da repetição (R2) e precisam ver o que acabou de entrar. */
    try {
      await chamada();
      limpar?.();
      /* Gravado: o botão se solta JÁ — a releitura não segura o segundo
         clique (que antes caía calado em `salvando`). */
      setSalvando(null);
      await r.recarregar();
      void Promise.resolve(aoMudar()).catch(() => undefined);
      return true;
    }
    catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  };
  const estufaMudou = () => window.dispatchEvent(new CustomEvent('labmanager:estufa-mudou'));
  const balancaDe = (r: UseRepeticao) => (r.dados?.atual?.balanca ?? r.dados?.inicial?.balanca)?.id;

  const faltaNoRascunho = (): string | null => {
    if (golpes == null) return 'Faltam os golpes.';
    if (!rascunho.cap) return 'Falta escolher a cápsula.';
    if (umido == null) return 'Falta a massa de cápsula + solo úmido.';
    return null;
  };

  const lancar = async (r: UseRepeticao) => {
    if (faltaNoRascunho() || salvando) return;
    const pontos = (r.dados?.atual?.pontos ?? []) as any[];
    const ordem = pontos.reduce((m, p) => Math.max(m, Number(p.ordem)), 0) + 1;
    const balanca = balancaDe(r);
    const ok = await executar('lancar', () => gravarLlPontos(id, {
      ...(balanca ? { balancaEquipamentoId: balanca } : {}),
      pontos: [{
        ordem,
        capsulaEquipamentoId: rascunho.cap,
        golpes: Math.round(golpes!),
        massaCapsulaSoloUmido: umido,
        ...(seco != null ? { massaCapsulaSoloSeco: seco } : {}),
      }],
    }), r, () => setRascunho(vazio()));
    if (ok && seco == null) estufaMudou();
  };

  const pesar = async (p: any, r: UseRepeticao) => {
    const massa = paraNumero(secas[p.id] ?? '');
    if (massa == null || salvando) return;
    const balanca = balancaDe(r);
    const ok = await executar(`seca-${p.id}`, () => pesarSeca({
      linhaId: p.id, massaCapsulaSoloSeco: massa,
      ...(balanca ? { balancaEquipamentoId: balanca } : {}),
    }), r);
    if (ok) {
      setSecas((s) => { const n = { ...s }; delete n[p.id]; return n; });
      estufaMudou();
    }
  };

  const comparacao = (inicial: any, atual: any): Comparacao => {
    const i = llDe(inicial);
    const a = llDe(atual);
    const ri = i != null ? Math.round(i) : null;
    const ra = a != null ? Math.round(a) : null;
    const dif = ri != null && ra != null ? ra - ri : null;
    const nI = pontosDoGrafico(inicial).length;
    const pesados = Number(atual?.cabecalho?.pesados ?? 0);
    return {
      inicial: { valor: llTexto(i), unidade: '%', nota: plural(nI, 'ponto', 'pontos') },
      atual: {
        valor: llTexto(a), unidade: '%',
        nota: `${plural(pesados, 'ponto', 'pontos')} com a seca (mínimo ${FAIXA.pontos})`,
      },
      diferenca: {
        valor: dif != null ? comSinal(dif) : '—',
        unidade: dif != null ? 'pontos' : undefined,
        nota: dif == null ? 'precisa de 2 pontos com a seca'
          : dif === 0 ? 'o mesmo LL'
          : `LL atual ${dif < 0 ? 'menor' : 'maior'} que o inicial`,
      },
    };
  };

  const lancamento = (r: UseRepeticao) => {
    const numeroDaTentativa = r.dados?.aberta?.numero;
    const pontos = [...((r.dados?.atual?.pontos ?? []) as any[])].sort((a, b) => a.ordem - b.ordem);
    const falta = faltaNoRascunho();
    const codigo = opcoes.find((o) => o.valor === rascunho.cap)?.codigo;

    const gravadas: LinhaDeLancamento[] = pontos.map((p) => {
      const morto = !vivo(p);
      const espera = naEstufa(p);
      return {
        chave: p.id,
        classe: morto ? 'descartada' : espera ? 'nova' : undefined,
        titulo: <>{p.golpes} golpes · <span className="cod">{p.capsula}</span></>,
        resultado: p.umidade != null ? <span className="n">{numero(p.umidade, 2)} %</span> : undefined,
        nota: morto ? `Descartado: ${p.motivo_descarte ?? ''}` : undefined,
        semCartao: p.umidade != null ? [0, 1, 4] : [0, 1],
        celulas: [
          <>{p.golpes}{p.fora_da_faixa_normativa && !morto
            ? <Selo tom="atencao">Fora de 15–35</Selo> : null}</>,
          <span className="cod">{p.capsula}</span>,
          numero(p.massa_capsula_solo_umido, 2),
          espera && lanca
            ? <span className="repeticao-seca">
                <CampoNumerico rotulo={`Seca + cápsula — ${p.capsula}`} rotuloOculto
                  placeholder="da estufa" value={secas[p.id] ?? ''}
                  disabled={salvando !== null}
                  onChange={(e) => setSecas((s) => ({ ...s, [p.id]: e.target.value }))} />
                <button type="button" className="botao secundario pequeno" data-gravar-linha=""
                  aria-disabled={!secas[p.id] || salvando !== null}
                  onClick={() => void pesar(p, r)}>
                  {salvando === `seca-${p.id}` ? 'Gravando…' : 'Gravar'}
                </button>
              </span>
            : p.massa_capsula_solo_seco != null ? numero(p.massa_capsula_solo_seco, 2) : '—',
          morto ? 'descartado'
            : espera ? <span className="sub">espera a seca</span>
            : <b>{numero(p.umidade, 2)}</b>,
          morto || !lanca ? null : (
            <button type="button" className="botao discreto pequeno"
              aria-label={`Descartar o ponto de ${p.golpes} golpes (${p.capsula})`}
              disabled={salvando !== null} onClick={() => setDescartando(p)}>
              Descartar
            </button>
          ),
        ],
      };
    });

    const nova: LinhaDeLancamento = {
      chave: 'novo',
      classe: 'nova',
      titulo: 'Ponto novo',
      resultado: wDigitado != null
        ? <span className="n repeticao-digitando">{numero(wDigitado, 2)} %</span> : undefined,
      semCartao: [4],
      celulas: [
        <CampoNumerico rotulo="Golpes do ponto novo" rotuloOculto inteiro value={rascunho.golpes}
          onChange={(e) => mexer('golpes', e.target.value)} />,
        <EscolhaPorCodigo rotulo="Cápsula do ponto novo" rotuloOculto opcoes={opcoes}
          valor={rascunho.cap}
          aoEscolher={(v) => mexer('cap', v)} />,
        <CampoNumerico rotulo="Úmida + cápsula do ponto novo (g)" rotuloOculto value={rascunho.umido}
          onChange={(e) => mexer('umido', e.target.value)} />,
        <CampoNumerico rotulo="Seca + cápsula do ponto novo (g)" rotuloOculto value={rascunho.seco}
          placeholder="ou depois"
          onChange={(e) => mexer('seco', e.target.value)} />,
        <b className="repeticao-digitando">{wDigitado != null ? numero(wDigitado, 2) : ''}</b>,
        <button type="button" className="botao secundario pequeno fe-gravar-linha" data-gravar-linha=""
          aria-disabled={falta !== null || salvando !== null}
          title={falta ?? 'Grava o ponto na repetição (Enter)'}
          onClick={() => void lancar(r)}>
          {salvando === 'lancar' ? 'Gravando…' : 'Gravar ponto'}
        </button>,
      ],
    };

    return (
      <BlocoDaFicha titulo={`Pontos novos · tentativa ${numeroDaTentativa ?? ''}`}
        sub={lanca
          ? 'Lance aqui mesmo. A tara vem do cadastro da cápsula. Sem a seca, o ponto espera a estufa (a seca também entra pela Bancada › Estufa).'
          : 'Os pontos da repetição, à medida que são lançados.'}>
        <div className="fe-tabela">
          <TabelaDeLancamento rotulo="Pontos da repetição — lançados e o novo"
            classe="capsulas fe-lanc repeticao-lanc"
            colunas={[...COLUNAS, { texto: '' }]} linhas={lanca ? [...gravadas, nova] : gravadas} />
          {!lanca && (
            <p className="sub repeticao-dica">
              {gravadas.length ? '' : 'Nenhum ponto lançado ainda. '}
              Lançar pede a permissão de lançar dados de ensaio, que o seu perfil não tem: os
              pontos chegam aqui quando o laboratório os lança na Pesagem úmida.
            </p>
          )}
          {lanca && <p className="sub repeticao-dica">
            {codigo && <>Tara da {codigo}: {tara != null ? `${numero(tara, 2)} g` : 'sem calibração interna'} · </>}
            faixa da NBR 6459: {FAIXA.min} a {FAIXA.max} golpes, mínimo de {FAIXA.pontos} pontos
            {foraDaFaixa && (
              <b className="repeticao-fora">
                {' · '}{rascunho.golpes} golpes está fora da faixa ({FAIXA.min} a {FAIXA.max}): o
                ponto entra, marcado, e a pendência avisa
              </b>
            )}
          </p>}
          <ErroDaApi erro={erro} />
        </div>
        <JanelaDeDescarte
          alvo={descartando ? `o ponto de ${descartando.golpes} golpes · ${descartando.capsula ?? ''}` : null}
          aoFechar={() => setDescartando(null)}
          aoConfirmar={async (motivo) => {
            const alvo = descartando;
            const ok = await executar('descarte', () => descartarLlPonto(id, alvo.id, motivo), r);
            if (ok && naEstufa(alvo)) estufaMudou();
            return ok;
          }}
          salvando={salvando !== null} />
      </BlocoDaFicha>
    );
  };

  const grafico = (r: UseRepeticao) => (
    <GraficoDaRepeticaoLl inicial={r.dados?.inicial} atual={r.dados?.atual}
      digitando={digitando} mostrarInicial={mostrarInicial}
      aoMostrarInicial={setMostrarInicial} />
  );

  const depois = (r: UseRepeticao) => {
    const ini = r.dados?.inicial;
    if (!ini) return null;
    const n = pontosDoGrafico(ini).length;
    return (
      <BlocoDaFicha titulo={`Pontos iniciais · tentativa ${r.dados?.vigente ?? ''}`}
        selo={<Selo tom="neutro">LL {llTexto(llDe(ini))} % · {plural(n, 'ponto', 'pontos')}</Selo>}
        sub="Em leitura durante a repetição. Se você adotar a nova, saem da conta e do laudo e ficam no histórico.">
        <div className="fe-tabela">
          <PontosDoLl pontos={ini.pontos ?? []} rotulo="Pontos iniciais" />
        </div>
      </BlocoDaFicha>
    );
  };

  return (
    <MolduraDaRepeticao ensaioId={id} ficha="limite-liquidez" titulo="Limite de liquidez"
      podeInterpretar={podeInterpretar} congelado={congelado} repeticao={rep}
      comparacao={comparacao} lancamento={lancamento} grafico={grafico} depois={depois}>
      {children}
    </MolduraDaRepeticao>
  );
}

// ══ o gráfico ═════════════════════════════════════════════════════════

/**
 * Reta de fluidez · inicial × atual. Os pontos levam `data-serie`
 * («inicial», «atual», «digitando») — é por eles que os testes leem.
 */
export function GraficoDaRepeticaoLl({ inicial, atual, digitando, mostrarInicial, aoMostrarInicial }: {
  inicial: any; atual: any; digitando: Ponto | null;
  mostrarInicial: boolean; aoMostrarInicial(v: boolean): void;
}) {
  const pI = pontosDoGrafico(inicial);
  const pA = pontosDoGrafico(atual);
  const rI = retaDe(inicial);
  const rA = retaDe(atual);
  const llA = llDe(atual);
  /* O ponto em digitação entra no enquadramento (arredondado, para a janela
     não tremer a cada tecla): fora dela ele sumiria do gráfico. */
  const dig = digitando && Number.isFinite(digitando.y) && digitando.x > 0
    ? { x: Math.round(digitando.x), y: Math.round(digitando.y) } : null;
  const todos = useMemo(() => {
    const v = [...pI, ...pA, ...(dig ? [dig] : [])];
    const ys = v.length ? v.map((p) => p.y) : [30, 60];
    const xs = v.map((p) => p.x);
    return [...v, { x: Math.min(12, ...xs), y: Math.min(...ys) },
      { x: Math.max(42, ...xs), y: Math.max(...ys) }];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pI), JSON.stringify(pA), dig?.x, dig?.y]);
  const z = useJanela(todos, 0.15, { x: 'log' });
  const pontaA = (r: { a: number; b: number }, n: number) => ({ x: n, y: r.a * Math.log(n) + r.b });

  return (
    <section className="bloco fe-bloco fe-grafico repeticao-grafico-bloco">
      <header className="fe-bloco-topo">
        <h2><span>Reta de fluidez · inicial × atual</span></h2>
        <label className="marcavel">
          <input type="checkbox" checked={mostrarInicial}
            onChange={(e) => aoMostrarInicial(e.target.checked)} />
          mostrar a inicial
        </label>
      </header>
      <div className="fe-miolo">
        <Plano janela={z.janela} escalaX="log" aoJanela={z.definir}
          tituloX="Número de golpes — N (escala log)" tituloY="w (%)">
          {({ px }) => (
            <>
              {mostrarInicial && rI && (
                <Reta a={pontaA(rI, 10)} b={pontaA(rI, 100)} px={px}
                  classe="reta repeticao-reta-inicial" />
              )}
              {rA && <Reta a={pontaA(rA, 10)} b={pontaA(rA, 100)} px={px}
                classe="reta repeticao-reta-atual" />}
              {mostrarInicial && pI.map((p, i) => {
                const q = px(p);
                return <circle key={`i${i}`} className="ponto repeticao-ponto-inicial"
                  data-serie="inicial" cx={q.x} cy={q.y} r={4.5} />;
              })}
              {pA.map((p, i) => {
                const q = px(p);
                return <circle key={`a${i}`} className="ponto" data-serie="atual"
                  cx={q.x} cy={q.y} r={5.5} />;
              })}
              {digitando && (() => {
                const q = px(digitando);
                return <circle className="ponto repeticao-ponto-digitando" data-serie="digitando"
                  cx={q.x} cy={q.y} r={6} />;
              })()}
              {llA != null && (
                <Achado p={{ x: 25, y: llA }} px={px} rotulo={`LL = ${llTexto(llA)} %`} />
              )}
            </>
          )}
        </Plano>
        <p className="fe-legenda">
          <span><i className="repeticao-l-inicial" />inicial (fora da conta)</span>
          <span><i className="l-aux" />reta inicial</span>
          <span><i className="repeticao-l-atual" />atual</span>
          <span><i className="repeticao-l-reta-atual" />reta atual</span>
          <span><i className="repeticao-l-digitando" />o ponto que você está digitando</span>
        </p>
        <p className="sub">
          O ponto entra no gráfico quando a umidade existe (úmida e seca lançadas). O que você
          está digitando aparece vazado e não entra na reta até «Gravar ponto».
        </p>
      </div>
    </section>
  );
}
